import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Collapse } from "antd";
import {
  CategoryFilter,
  CategoryValidation,
  ResponseFields,
  ShareCategory,
  ShareProduct,
} from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import { message, Form } from "@dreambigger/design-system";
import StepWrapper from "./step-wrapper";
import { ShareProductsStepAssets } from "@dreambigger/shared/src/types";
import { ProductCheckbox } from "../components";
import "@dreambigger/design-system";
import { useApplication, useShares } from "../api";
import { Conditional } from "@dreambigger/conditional";
import {
  hasVisibleSelection,
  getUnmetVisibleCategories as computeUnmetVisibleCategories,
  buildMinSelectionError,
} from "./shareProductsValidation";

// Hidden form field used to enforce per-category minimum selections on submit.
// Its rejecting validator blocks the step from advancing and surfaces the error
// message, while the continue button itself stays enabled.
const MIN_SELECTION_FIELD = "__categoryMinSelection";

export default function StepProducts({
  flow,
  step,
  brand,
  progress,
}: StepProps) {
  const assets: ShareProductsStepAssets = step.assets;
  const { radioSelection, categoryValidation } = assets;

  // State for managing whether the submit button is enabled.
  const [disabled, setDisabled] = useState(true);
  const [activePanelKey, setActivePanelKey] =
    useState<string[] | string | undefined>(undefined);
  const [resetAllExcept, setResetAllExcept] = useState("");
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    {}
  );
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper.data, flow.id]
  );

  // Fetch share categories using the new hook
  const { shareCategories, isLoading, error } = useShares(
    flow.financialInstitution.id,
    application?.id
  );

  // Process and sort the share categories data
  const pCategoryData = useMemo(() => {
    if (!shareCategories) return [];
    return shareCategories.map((pCategory: ShareCategory) => ({
      ...pCategory,
      products: pCategory.products?.sort((a: ShareProduct, b: ShareProduct) =>
        a.title.localeCompare(b.title, undefined, { numeric: true })
      ),
    }));
  }, [shareCategories]);

  const response = useMemo(
    () => applicationHelper.findResponse(flow.id, "draft", step.slug),
    [applicationHelper, flow.id, step.slug]
  );

  const [form] = Form.useForm();
  const lastStepSlugRef = useRef<string>();

  // Watch all form values to detect when they change
  const formValues = Form.useWatch([], form);

  // Memoized function to recalculate counts from form values
  const recalculateCounts = useCallback(() => {
    if (!form || !pCategoryData.length) return {};

    const newCounts: Record<string, number> = {};
    pCategoryData.forEach((category) => {
      category.products?.forEach((product) => {
        const fieldValue = form.getFieldValue(product.slug);
        if (fieldValue === true) {
          newCounts[category.slug] = (newCounts[category.slug] || 0) + 1;
        }
      });
    });
    return newCounts;
  }, [pCategoryData, form]);

  // Reset the category counts when the step changes (not on every re-render)
  useEffect(() => {
    const currentStepSlug = step.slug;

    // Only reset if we actually changed steps
    if (lastStepSlugRef.current !== currentStepSlug) {
      lastStepSlugRef.current = currentStepSlug;
      setCategoryCounts({});
    }
  }, [step.slug]);

  // When using category validation, keep the continue button enabled and clear the
  // minimum-selection error as soon as every visible category's minimum is met.
  useEffect(() => {
    if (!categoryValidation) return;
    checkSubmitDisabled();
    const hasError = form.getFieldError(MIN_SELECTION_FIELD).length > 0;
    if (hasError && getUnmetVisibleCategories().length === 0) {
      form.setFields([{ name: MIN_SELECTION_FIELD, errors: [] }]);
    }
  }, [categoryCounts]);

  // Set Products to be initially opened or closed. This can be defined in the config at the specific category level or across the whole step.
  // Calculate directly in useMemo to avoid hydration mismatch
  const initialActivePanelKeys = useMemo(() => {
    if (!pCategoryData.length) return [];

    return pCategoryData
      .filter((category) => {
        // For each category, look up the corresponding validation in the step assets if it exists.
        const correspondingValidation = categoryValidation?.find(
          (validation) => validation.slug === category.slug
        );
        // if collapse behavior is set at the category level, use that
        if (
          correspondingValidation?.setCollapse !== undefined &&
          [true, false].includes(correspondingValidation.setCollapse)
        ) {
          return !correspondingValidation.setCollapse;
        }
        // if collapse behavior is set at the asset level, use that
        if (
          assets?.setCollapse !== undefined &&
          [true, false].includes(assets.setCollapse)
        ) {
          return !assets.setCollapse;
        }
        // by default, stay open
        return true;
      })
      .map((category) => category.uuid);
  }, [assets.setCollapse, pCategoryData, categoryValidation]);

  // Update activePanelKey when initialActivePanelKeys changes, but only if user hasn't manually changed it
  useEffect(() => {
    if (initialActivePanelKeys.length > 0 && activePanelKey === undefined) {
      setActivePanelKey(initialActivePanelKeys);
    }
  }, [initialActivePanelKeys, activePanelKey]);

  // Handle loading error
  useEffect(() => {
    if (error) {
      console.error(error);
      message.error(
        "There was a problem loading this page. Please contact support."
      );
    }
  }, [error]);

  // Show or hide panel when clicked
  function handlePanelChange(keys: string | string[]) {
    setActivePanelKey(keys);
  }

  const handleProductChange = (
    value: boolean,
    productSlug: string,
    categorySlug: string
  ) => {
    if (value === false) {
      setCategoryCounts((prevCounts) => ({
        ...prevCounts,
        [categorySlug]: (prevCounts[categorySlug] || 1) - 1,
      }));
    } else if (form.getFieldValue(productSlug)) {
      // Update the category count
      setCategoryCounts((prevCounts) => ({
        ...prevCounts,
        [categorySlug]: (prevCounts[categorySlug] || 0) + 1,
      }));
    }
    // If category validation is enabled, end the function here and call disable check
    // from useEffect in next render cycle. Otherwise, check now.
    if (categoryValidation) {
      return;
    }
    checkSubmitDisabled();
  };

  const checkSubmitDisabled = () => {
    // At least one product must always be selected before the applicant can
    // advance — including when category validation is configured with no
    // per-category minimum. Per-category minimums greater than one remain
    // enforced on submit via MIN_SELECTION_FIELD: once something is selected the
    // button stays enabled and the validator surfaces an error message rather
    // than a silently-disabled button.
    setDisabled(!hasVisibleSelection(visibleCategories, recalculateCounts()));
  };

  const handlePrefill = useCallback(
    (initialValues?: ResponseFields) => {
      if (!initialValues || !pCategoryData.length) {
        return initialValues;
      }
      return initialValues;
    },
    [pCategoryData]
  );

  // Handle prefilling and initialize required products when products load
  useEffect(() => {
    if (pCategoryData.length && form) {
      const valuesToSet: ResponseFields = {};

      // First, set all required products to true
      pCategoryData.forEach((category) => {
        category.products?.forEach((product) => {
          if (product.required) {
            valuesToSet[product.slug] = true;
          }
        });
      });

      // Then, overlay any saved response values (required products will remain true)
      if (response?.fields) {
        const prefillResult = handlePrefill(response.fields);
        if (prefillResult) {
          Object.assign(valuesToSet, prefillResult);
        }
      }

      // Set all values at once
      if (Object.keys(valuesToSet).length > 0) {
        // Use setTimeout to ensure this happens after React's reconciliation
        setTimeout(() => {
          form.setFieldsValue(valuesToSet);
          // Note: counts will be recalculated automatically via useWatch effect
        }, 0);
      }
    }
  }, [response?.fields, pCategoryData.length, form, handlePrefill]);

  // Recalculate counts and check disabled state whenever form values change
  useEffect(() => {
    if (pCategoryData.length && form && formValues !== undefined) {
      // Recalculate category counts based on current form values
      const newCounts = recalculateCounts();
      setCategoryCounts(newCounts);

      // Check if submit button should be enabled
      checkSubmitDisabled();
    }
  }, [formValues, pCategoryData.length, form, recalculateCounts]);

  // If Category Validation has been added to the config for this, check if the category should be filtered out.
  const isValidCategory = (categoryName: string) => {
    if (categoryValidation) {
      return categoryValidation.some(
        (category) => category.slug === categoryName
      );
    }
    return true;
  };

  // If maximum number of products is set, only allow currently checked products to be unchecked.
  const preventMoreSelections = useCallback(
    (productSlug: string, categorySlug: string) => {
      // We never need to prevent additional selections if there is no category validation.
      if (!categoryValidation) {
        return false;
      }
      // Search array for selected category
      const category = categoryValidation.find(
        (searchedCategory) => searchedCategory.slug === categorySlug
      );
      // Guard clause for if category is not found OR has no max limit
      if (!category || !category.max) {
        return false;
      }
      // Prevent button from being clicked if the max number of products has been selected
      // and the product is not already selected.
      const isProductSelected = form.getFieldValue(productSlug) === true;
      const currentCount = categoryCounts[categorySlug] || 0;
      const shouldPrevent = currentCount >= category.max && !isProductSelected;

      return shouldPrevent;
    },
    [categoryValidation, categoryCounts, form]
  );

  // Determine whether a category passes its configured visibility filter.
  // (AND conditions are expressed as an array of arrays, OR conditions as a
  // single array — mirrors the inline filter that used to live in the render.)
  const categoryPassesFilter = useCallback(
    (pCategory: ShareCategory) => {
      const filter = assets.categoryValidation?.find(
        (category: CategoryValidation) => category.slug === pCategory.slug
      )?.filter;
      if (!filter?.length) {
        return true;
      }
      const matches = ({ slug, fieldKey, fieldValue }: CategoryFilter) => {
        const savedResponse = application?.responses.find(
          (response) => response.slug === slug
        );
        const fields = savedResponse?.fields || {};
        return Conditional.evaluate({
          conditions: { [fieldKey]: fieldValue },
          data: fields,
        });
      };
      if (Array.isArray(filter[0])) {
        return (filter[0] as CategoryFilter[]).every(matches);
      }
      return (filter as CategoryFilter[]).some(matches);
    },
    [assets.categoryValidation, application?.responses]
  );

  // The categories actually rendered to the applicant: those that pass their
  // visibility filter, contain at least one product, and (when category
  // validation is configured) belong to that validation set. Minimum-selection
  // rules only apply to these — a fully hidden category is never enforced.
  const visibleCategories = useMemo(
    () =>
      pCategoryData
        .filter(categoryPassesFilter)
        .filter(
          (pCategory) =>
            !!pCategory.products?.length && isValidCategory(pCategory.slug)
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [pCategoryData, categoryPassesFilter, categoryValidation]
  );

  // Visible categories whose minimum selection count has not yet been met.
  // Counts are recomputed from the live form values so the result is accurate
  // at submit time regardless of the categoryCounts render state.
  const getUnmetVisibleCategories = useCallback(
    () =>
      computeUnmetVisibleCategories(
        visibleCategories,
        categoryValidation,
        recalculateCounts()
      ),
    [categoryValidation, visibleCategories, recalculateCounts]
  );

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    handlePrefill,
    handleInput: () => {}, // hack to fix prefill on refresh
    disabled,
    processing: isLoading,
  };

  // ----- FINAL RENDER ------

  return (
    <StepWrapper {...wrapperProps}>
      <Collapse
        bordered={false}
        activeKey={activePanelKey ?? []}
        ghost
        onChange={handlePanelChange}
      >
        {visibleCategories.map((pCategory: ShareCategory) => (
          <Collapse.Panel
            key={pCategory.uuid}
            header={
              <h3 className="f-4 fwt fwb f-4 ttu textPrimary bg-primary w-100 pl-2 br-2 s-3 mb-0">
                {pCategory.title}{" "}
              </h3>
            }
            className="ao-collapse-panel"
            forceRender
          >
            <div className="mb-2">
              {pCategory.products &&
                pCategory.products
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((product: ShareProduct) => (
                    <ProductCheckbox
                      key={product.uuid}
                      product={product}
                      accentColor={brand.primaryColor}
                      form={form}
                      onChange={(newValue) =>
                        handleProductChange(
                          newValue,
                          product.slug,
                          pCategory.slug
                        )
                      }
                      radioSelection={radioSelection}
                      setResetAllExcept={setResetAllExcept}
                      resetAllExcept={resetAllExcept}
                      disabled={preventMoreSelections(
                        product.slug,
                        pCategory.slug
                      )}
                    />
                  ))}
            </div>
          </Collapse.Panel>
        ))}
      </Collapse>
      {/* Enforces per-category minimums on submit without disabling the button.
          Never persisted: value stays undefined (we only set errors), which
          StepWrapper's defaultHandleNext strips before saving. */}
      {categoryValidation && (
        <Form.Item
          name={MIN_SELECTION_FIELD}
          className="mb-0"
          rules={[
            {
              validator: () => {
                const unmet = getUnmetVisibleCategories();
                return unmet.length === 0
                  ? Promise.resolve()
                  : Promise.reject(new Error(buildMinSelectionError(unmet)));
              },
            },
          ]}
        >
          <input type="hidden" />
        </Form.Item>
      )}
    </StepWrapper>
  );
}
